import React, { useState, useEffect } from "react";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useModalEscape from "../../hooks/useModalEscape";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  onSnapshot,
  getDoc,
  doc as firestoreDoc,
} from "firebase/firestore";
import Sidebar from "../../components/Sidebar/Sidebar";
import { useDocumentUpload } from "../../context/DocumentUploadContext";
import {
  fetchDocuments,
  deleteDocument,
  translateDocumentFilipino,
  fetchDocumentFilipinoTranslation,
  confirmDocuments,
  normalizeOrdinanceNumberForMatch,
  normalizeTitleForMatch,
} from "../../services/documentService";
import { toast } from "react-toastify";
import DocumentComments from '../../components/DocumentComments/DocumentComments';
import DocumentManagementAiDisclaimer from "../../components/DocumentManagementAiDisclaimer/DocumentManagementAiDisclaimer";
import {
  formatAiUserFacingError,
  getDocumentAnalysisFormValidation,
  validateDraftDocument,
  normalizeDraftDocument,
  getAdminVerificationBadgeText,
} from "../../utils/documentManagementUi";
import "./SA_Overview2.css";

import {
  Activity,
  Users,
  Settings,
  FileText,
  Shield,
  Server,
  ArrowLeft,
  Search,
  Upload,
  File,
  X,
  ChevronLeft,
  ChevronRight,
  Trash2,
  AlertTriangle,
  Filter,
} from "lucide-static";

const avisaLogo = `${process.env.PUBLIC_URL}/images/avisa.svg`;

const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    if (size) {
      svgString = svgString.replace(
        "<svg",
        `<svg width="${size}" height="${size}"`,
      );
    }
    if (color) {
      svgString = svgString.replace(
        "<svg",
        `<svg color="${color}" stroke="${color}"`,
      );
    }
    if (className) {
      svgString = svgString.replace("<svg", `<svg class="${className}"`);
    }
    return svgString;
  };
  return (
    <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />
  );
};

const SA_Overview2 = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);

  const {
    isScanning,
    uploadingFiles,
    uploadProgress,
    uploadPhase,
    uploadDocuments,
    draftDocuments,
    setDraftDocuments,
    clearDraftDocuments,
    isDraftReviewOpen,
    setIsDraftReviewOpen,
    activeDraftIndex,
    setActiveDraftIndex,
  } = useDocumentUpload();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploadsModalOpen, setIsUploadsModalOpen] = useState(false);
  const [uploaderCache, setUploaderCache] = useState({});

  const removeSelectedFile = (index) => {
    if (isScanning) return;
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearSelectedFiles = () => {
    if (isScanning) return;
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const { allDocuments, documentsLoaded } = useData();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  // Dynamically compute unique categories from all documents in the database
  const categoryOptions = React.useMemo(() => {
    const knownDatabaseCategories = [
      "Business",
      "Consumer Rights",
      "Education",
      "Environment",
      "Governance",
      "Health",
      "Infrastructure",
      "Labor",
      "Peace and Order",
      "Public Safety",
      "Social Services",
      "Taxation",
      "Tourism",
      "Traffic",
      "Zoning & Land Use",
    ];
    const categories = new Set(knownDatabaseCategories);
    (allDocuments || []).forEach((doc) => {
      if (doc.category && typeof doc.category === "string" && doc.category.trim()) {
        categories.add(doc.category.trim());
      }
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [allDocuments]);
  const defaultFilters = {
    category: "",
    legalLevel: "",
    province: "",
    municipality: "",
    barangay: "",
  };
  const [filterOptions, setFilterOptions] = useState({
    category: "",
    legalLevel: "",
    province: "",
    municipality: "",
    barangay: "",
  });
  const [pendingFilterOptions, setPendingFilterOptions] = useState({
    category: "",
    legalLevel: "",
    province: "",
    municipality: "",
    barangay: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const [isDocInfoModalOpen, setIsDocInfoModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [docViewMode, setDocViewMode] = useState("original");
  const [summaryLanguage, setSummaryLanguage] = useState("en");
  const [filipinoTranslation, setFilipinoTranslation] = useState(null);
  const [isTranslationLoading, setIsTranslationLoading] = useState(false);
  const [currentDraftIndex, setCurrentDraftIndex] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");
  const [editedKeyPoints, setEditedKeyPoints] = useState([]);
  const [editedFilipinoSummary, setEditedFilipinoSummary] = useState("");
  const [editedFilipinoKeyPoints, setEditedFilipinoKeyPoints] = useState([]);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedFilipinoTitle, setEditedFilipinoTitle] = useState("");
  const [editedOrdinanceNumber, setEditedOrdinanceNumber] = useState("");
  const [editedScopeLevel, setEditedScopeLevel] = useState("");
  const [editedCategory, setEditedCategory] = useState("");
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [editedCitation, setEditedCitation] = useState("");
  const [editedSourceType, setEditedSourceType] = useState("office");
  const [editedSourceUrl, setEditedSourceUrl] = useState("");
  const [isSavingEdits, setIsSavingEdits] = useState(false);

  const analysisFormValidation = React.useMemo(
    () =>
      getDocumentAnalysisFormValidation({
        editedTitle,
        editedFilipinoTitle,
        editedOrdinanceNumber,
        editedScopeLevel,
        editedSummary,
        editedFilipinoSummary,
        editedCategory,
        editedKeyPoints,
        editedFilipinoKeyPoints,
        summaryLanguage,
        editedCitation,
        editedSourceUrl,
      }),
    [
      editedTitle,
      editedFilipinoTitle,
      editedOrdinanceNumber,
      editedScopeLevel,
      editedSummary,
      editedFilipinoSummary,
      editedCategory,
      editedKeyPoints,
      editedFilipinoKeyPoints,
      summaryLanguage,
      editedCitation,
      editedSourceUrl,
    ],
  );
  const canSubmitAnalysisForm = analysisFormValidation.ok;

  const queueHasIncompleteDrafts = React.useMemo(() => {
    if (!draftDocuments || draftDocuments.length === 0) return false;
    return draftDocuments.some((draft, idx) => {
      if (idx === currentDraftIndex) {
        return !canSubmitAnalysisForm;
      }
      return !validateDraftDocument(draft).ok;
    });
  }, [draftDocuments, currentDraftIndex, canSubmitAnalysisForm]);

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!canSubmitAnalysisForm) {
        toast.error(analysisFormValidation.message);
      }
    }
  };

  const closeAllModals = () => {
    setShowFilterModal(false);
    closeDocInfoModal();
    closeDeleteModal();
    setIsUploadsModalOpen(false);
  };

  useModalEscape(closeAllModals, showFilterModal || isDocInfoModalOpen || isDeleteModalOpen || isUploadsModalOpen);

  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const openDraftDocument = (draftDoc, index = 0, currentQueue = draftDocuments) => {
    draftDoc = normalizeDraftDocument(draftDoc);
    draftEditsRef.current.isConfirmedOrDeleted = false;
    setSelectedDoc(draftDoc);
    setCurrentDraftIndex(index);
    setActiveDraftIndex(index);
    setIsDraftReviewOpen(true);
    setDocViewMode("summary");
    setSummaryLanguage("en");
    setIsTranslationLoading(false);

    setEditedTitle(draftDoc.title || "");
    setEditedOrdinanceNumber(draftDoc.ordinanceNumber || "");
    setEditedScopeLevel(
      draftDoc.legalLevel ||
        (draftDoc.location?.barangay
          ? "Barangay"
          : draftDoc.location?.municipality
            ? "Municipal"
            : "National")
    );
    setEditedCategory(draftDoc.category || "");
    setIsCustomCategory(false);
    setEditedSummary(draftDoc.summary || draftDoc.purpose || "");
    setEditedKeyPoints(
      Array.isArray(draftDoc.keyPoints) ? [...draftDoc.keyPoints] : []
    );

    const filTranslation = draftDoc.filipinoTranslation || {};
    setFilipinoTranslation(filTranslation);
    setEditedFilipinoTitle(filTranslation.title || draftDoc.title || "");
    setEditedFilipinoSummary(
      filTranslation.summary || draftDoc.summary || draftDoc.purpose || ""
    );
    setEditedFilipinoKeyPoints(
      Array.isArray(filTranslation.keyPoints)
        ? [...filTranslation.keyPoints]
        : Array.isArray(draftDoc.keyPoints)
          ? [...draftDoc.keyPoints]
          : []
    );

    const initialCitation = draftDoc.citation || draftDoc.ordinanceNumber || draftDoc.title || "";
    setEditedCitation(initialCitation);
    const initialSource = draftDoc.sourceUrl || draftDoc.source || "";
    const isOnline = initialSource.startsWith("http://") || initialSource.startsWith("https://") || draftDoc.sourceType === "online";
    setEditedSourceType(isOnline ? "online" : "office");
    setEditedSourceUrl(initialSource || (isOnline ? "" : "Office of the Sangguniang Bayan Records"));

    setIsEditMode(true);
    setIsDocInfoModalOpen(true);
  };

  // Automatically restore draft confirmation modal if draft review was open or drafts are pending
  useEffect(() => {
    if (
      isDraftReviewOpen &&
      Array.isArray(draftDocuments) &&
      draftDocuments.length > 0 &&
      !isDocInfoModalOpen
    ) {
      const targetIndex =
        activeDraftIndex >= 0 && activeDraftIndex < draftDocuments.length
          ? activeDraftIndex
          : 0;
      const targetDoc = draftDocuments[targetIndex] || draftDocuments[0];
      if (targetDoc) {
        openDraftDocument(targetDoc, targetIndex, draftDocuments);
      }
    }
  }, [isDraftReviewOpen, draftDocuments, activeDraftIndex, isDocInfoModalOpen]);

  const detectedDuplicate = React.useMemo(() => {
    if (!selectedDoc?._isDraft) return null;
    const normOrd = normalizeOrdinanceNumberForMatch(editedOrdinanceNumber);
    const normTitle = normalizeTitleForMatch(editedTitle);
    const filename = String(selectedDoc.filename || "").toLowerCase().trim();

    return (allDocuments || []).find((d) => {
      const existOrd = normalizeOrdinanceNumberForMatch(d.ordinanceNumber);
      if (normOrd && existOrd && normOrd.length >= 2 && normOrd === existOrd) {
        return true;
      }
      const existTitle = normalizeTitleForMatch(d.title);
      if (normTitle && existTitle && normTitle.length >= 20 && normTitle === existTitle) {
        return true;
      }
      if (filename && d.filename && filename.length >= 5 && filename === String(d.filename).toLowerCase().trim()) {
        return true;
      }
      return false;
    });
  }, [selectedDoc, editedOrdinanceNumber, editedTitle, allDocuments]);

  const queueHasDuplicates = React.useMemo(() => {
    return draftDocuments.some((queuedDraft, idx) => {
      const draft = idx === currentDraftIndex
        ? { ...queuedDraft, title: editedTitle, ordinanceNumber: editedOrdinanceNumber }
        : queuedDraft;
      const normOrd = normalizeOrdinanceNumberForMatch(draft.ordinanceNumber);
      const normTitle = normalizeTitleForMatch(draft.title);
      const filename = String(draft.filename || "").toLowerCase().trim();

      return (allDocuments || []).some((d) => {
        const existOrd = normalizeOrdinanceNumberForMatch(d.ordinanceNumber);
        if (normOrd && existOrd && normOrd.length >= 2 && normOrd === existOrd) return true;
        const existTitle = normalizeTitleForMatch(d.title);
        if (normTitle && existTitle && normTitle.length >= 20 && normTitle === existTitle) return true;
        if (filename && d.filename && filename.length >= 5 && filename === String(d.filename).toLowerCase().trim()) return true;
        return false;
      });
    });
  }, [draftDocuments, allDocuments, currentDraftIndex, editedTitle, editedOrdinanceNumber]);

  const draftEditsRef = React.useRef({ isConfirmedOrDeleted: false });

  const saveCurrentDraftEdits = () => {
    if (draftEditsRef.current.isConfirmedOrDeleted) return;
    if (!selectedDoc?._isDraft) return;

    setDraftDocuments((prevDrafts) => {
      const currentQueue = Array.isArray(prevDrafts) ? [...prevDrafts] : [];
      if (currentQueue.length === 0) return currentQueue;

      let targetIdx = currentQueue.findIndex((d) => d && selectedDoc?.id && d.id === selectedDoc.id);
      if (targetIdx === -1 && currentDraftIndex >= 0 && currentDraftIndex < currentQueue.length) {
        targetIdx = currentDraftIndex;
      }
      if (targetIdx === -1) return currentQueue;

      currentQueue[targetIdx] = {
        ...currentQueue[targetIdx],
        title: editedTitle.trim() || currentQueue[targetIdx].title,
        ordinanceNumber: editedOrdinanceNumber.trim(),
        legalLevel: editedScopeLevel.trim() || currentQueue[targetIdx].legalLevel,
        category: editedCategory.trim() || currentQueue[targetIdx].category,
        citation: editedCitation.trim() || editedOrdinanceNumber.trim(),
        sourceUrl: editedSourceUrl.trim() || (editedSourceType === "office" ? "Office of the Sangguniang Bayan Records" : ""),
        sourceType: editedSourceType,
        summary: summaryLanguage === "en" ? editedSummary.trim() : (currentQueue[targetIdx].summary || editedSummary.trim()),
        keyPoints: summaryLanguage === "en" ? editedKeyPoints.filter(p => p && p.trim()) : (currentQueue[targetIdx].keyPoints || editedKeyPoints.filter(p => p && p.trim())),
        purpose: editedSummary.trim() || currentQueue[targetIdx].purpose,
        filipinoTranslation: {
          ...(currentQueue[targetIdx].filipinoTranslation || {}),
          title: editedFilipinoTitle.trim() || editedTitle.trim(),
          summary: editedFilipinoSummary.trim() || editedSummary.trim(),
          keyPoints: (editedFilipinoKeyPoints.length > 0 ? editedFilipinoKeyPoints : editedKeyPoints).filter(p => p && p.trim()),
          ordinanceNumber: editedOrdinanceNumber.trim(),
          legalLevel: editedScopeLevel.trim() || currentQueue[targetIdx].legalLevel,
        }
      };
      return currentQueue;
    });
  };

  draftEditsRef.current = {
    isConfirmedOrDeleted: draftEditsRef.current.isConfirmedOrDeleted,
    selectedDoc,
    currentDraftIndex,
    draftDocuments,
    editedTitle,
    editedFilipinoTitle,
    editedOrdinanceNumber,
    editedScopeLevel,
    editedCategory,
    editedSummary,
    editedFilipinoSummary,
    editedKeyPoints,
    editedFilipinoKeyPoints,
    summaryLanguage,
  };

  useEffect(() => {
    return () => {
      const state = draftEditsRef.current;
      if (!state.isConfirmedOrDeleted && state.selectedDoc?._isDraft) {
        setDraftDocuments((prevDrafts) => {
          const currentQueue = Array.isArray(prevDrafts) ? [...prevDrafts] : [];
          if (currentQueue.length === 0) return currentQueue;

          let targetIdx = currentQueue.findIndex((d) => d && state.selectedDoc?.id && d.id === state.selectedDoc.id);
          if (targetIdx === -1 && state.currentDraftIndex >= 0 && state.currentDraftIndex < currentQueue.length) {
            targetIdx = state.currentDraftIndex;
          }
          if (targetIdx === -1) return currentQueue;

          currentQueue[targetIdx] = {
            ...currentQueue[targetIdx],
            title: (state.editedTitle || "").trim() || currentQueue[targetIdx].title,
            ordinanceNumber: (state.editedOrdinanceNumber || "").trim(),
            legalLevel: (state.editedScopeLevel || "").trim() || currentQueue[targetIdx].legalLevel,
            category: (state.editedCategory || "").trim() || currentQueue[targetIdx].category,
            summary: state.summaryLanguage === "en"
              ? (state.editedSummary || "").trim()
              : (currentQueue[targetIdx].summary || (state.editedSummary || "").trim()),
            keyPoints: state.summaryLanguage === "en"
              ? (state.editedKeyPoints || []).filter((p) => p && String(p).trim())
              : (currentQueue[targetIdx].keyPoints || (state.editedKeyPoints || []).filter((p) => p && String(p).trim())),
            purpose: (state.editedSummary || "").trim() || currentQueue[targetIdx].purpose,
            filipinoTranslation: {
              ...(currentQueue[targetIdx].filipinoTranslation || {}),
              title: (state.editedFilipinoTitle || "").trim() || (state.editedTitle || "").trim(),
              summary: (state.editedFilipinoSummary || "").trim() || (state.editedSummary || "").trim(),
              keyPoints: ((state.editedFilipinoKeyPoints || []).length > 0 ? state.editedFilipinoKeyPoints : state.editedKeyPoints || []).filter((p) => p && String(p).trim()),
              ordinanceNumber: (state.editedOrdinanceNumber || "").trim(),
              legalLevel: (state.editedScopeLevel || "").trim() || currentQueue[targetIdx].legalLevel,
            },
          };
          return currentQueue;
        });
      }
    };
  }, []);

  const handleConfirmUpload = async () => {
    if (selectedFiles.length === 0 || isScanning) {
      toast.error("Please select at least one file before uploading.");
      return;
    }

    await uploadDocuments(selectedFiles, () => {
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    });
  };

  const handleConfirmDetails = async () => {
    if (!selectedDoc || isSavingEdits) return;

    if (detectedDuplicate) {
      toast.error(
        `Duplicate Document: Document with number '${detectedDuplicate.ordinanceNumber || detectedDuplicate.title}' already exists in the database. Please discard it or change its details.`
      );
      return;
    }

    if (!canSubmitAnalysisForm) {
      toast.error(analysisFormValidation.message);
      return;
    }

    setIsSavingEdits(true);
    try {
      const validationBadge = getAdminVerificationBadgeText(user);

      const confirmedDoc = {
        ...selectedDoc,
        title: editedTitle.trim() || selectedDoc.title || "Untitled Document",
        ordinanceNumber: editedOrdinanceNumber.trim(),
        legalLevel: editedScopeLevel.trim() || selectedDoc.legalLevel || "National",
        category: editedCategory.trim() || selectedDoc.category || "Uncategorized",
        citation: editedCitation.trim() || editedOrdinanceNumber.trim(),
        sourceUrl: editedSourceUrl.trim() || (editedSourceType === "office" ? "Office of the Sangguniang Bayan Records" : ""),
        sourceType: editedSourceType,
        verifiedBy: validationBadge,
        verifiedAt: new Date().toISOString(),
        summary: editedSummary.trim() || selectedDoc.summary || selectedDoc.purpose || "",
        keyPoints: editedKeyPoints.filter((p) => p && p.trim()),
        purpose: editedSummary.trim() || selectedDoc.purpose || "",
        filipinoTranslation: {
          ...(selectedDoc.filipinoTranslation || {}),
          title: editedFilipinoTitle.trim() || editedTitle.trim(),
          summary: editedFilipinoSummary.trim() || editedSummary.trim(),
          keyPoints: (editedFilipinoKeyPoints.length > 0 ? editedFilipinoKeyPoints : editedKeyPoints).filter((p) => p && p.trim()),
          ordinanceNumber: editedOrdinanceNumber.trim(),
          legalLevel: editedScopeLevel.trim() || selectedDoc.legalLevel || "National",
        },
      };

      await confirmDocuments([confirmedDoc]);

      toast.success("Document confirmed and saved successfully!");

      const confirmedDraftId = selectedDoc?.id;
      const remainingDrafts = draftDocuments.filter(
        (d, idx) => {
          if (confirmedDraftId && (d.id === confirmedDraftId || d._id === confirmedDraftId)) return false;
          if (d === selectedDoc) return false;
          if (idx === currentDraftIndex) return false;
          return true;
        }
      ).map((d, i) => ({ ...d, _draftQueueIndex: i }));

      if (remainingDrafts.length > 0) {
        setDraftDocuments(remainingDrafts);
        const nextIndex = Math.min(currentDraftIndex, remainingDrafts.length - 1);
        setActiveDraftIndex(nextIndex);
        openDraftDocument(remainingDrafts[nextIndex], nextIndex, remainingDrafts);
      } else {
        draftEditsRef.current.isConfirmedOrDeleted = true;
        clearDraftDocuments();
        closeDocInfoModal(false);
      }

      await fetchDocuments();
    } catch (err) {
      console.error("Failed to confirm document:", err);
      toast.error(formatAiUserFacingError(err.message || "Failed to confirm and save document."));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleConfirmAllDrafts = async () => {
    if (draftDocuments.length === 0 || isSavingEdits) return;

    if (!canSubmitAnalysisForm) {
      toast.error(analysisFormValidation.message);
      return;
    }

    if (queueHasIncompleteDrafts) {
      toast.error(
        "Some queued documents have missing details. Please review each document before confirming all."
      );
      return;
    }

    if (queueHasDuplicates) {
      toast.error(
        "Some documents in the queue already exist in the database. Please review, edit, or discard duplicate documents before confirming."
      );
      return;
    }

    saveCurrentDraftEdits();

    setIsSavingEdits(true);
    try {
      const validationBadge = getAdminVerificationBadgeText(user);

      const allConfirmed = draftDocuments.map((queuedDraft, idx) => {
        const draft = normalizeDraftDocument(queuedDraft);
        const isCurrent = idx === currentDraftIndex;
        const draftCitation = isCurrent
          ? (editedCitation.trim() || draft.citation || editedOrdinanceNumber.trim())
          : (draft.citation || draft.ordinanceNumber || "");
        const draftSource = isCurrent
          ? (editedSourceUrl.trim() || (editedSourceType === "office" ? "Office of the Sangguniang Bayan Records" : ""))
          : (draft.sourceUrl || draft.source || "Office of the Sangguniang Bayan Records");
        const draftSourceType = isCurrent
          ? editedSourceType
          : (draft.sourceType || (String(draftSource).startsWith("http") ? "online" : "office"));

        return {
          ...draft,
          title: isCurrent ? (editedTitle.trim() || draft.title || "Untitled Document") : draft.title,
          ordinanceNumber: isCurrent ? editedOrdinanceNumber.trim() : draft.ordinanceNumber,
          legalLevel: isCurrent ? (editedScopeLevel.trim() || draft.legalLevel || "National") : draft.legalLevel,
          category: isCurrent ? (editedCategory.trim() || draft.category || "Uncategorized") : draft.category,
          citation: draftCitation,
          sourceUrl: draftSource,
          sourceType: draftSourceType,
          verifiedBy: validationBadge,
          verifiedAt: new Date().toISOString(),
          summary: isCurrent ? (editedSummary.trim() || draft.summary || draft.purpose || "") : draft.summary,
          keyPoints: isCurrent ? editedKeyPoints.filter((p) => p && p.trim()) : draft.keyPoints,
          purpose: isCurrent ? (editedSummary.trim() || draft.purpose || "") : draft.purpose,
          filipinoTranslation: {
            ...(draft.filipinoTranslation || {}),
            title: isCurrent ? (editedFilipinoTitle.trim() || editedTitle.trim()) : (draft.filipinoTranslation?.title || draft.title),
            summary: isCurrent ? (editedFilipinoSummary.trim() || editedSummary.trim()) : (draft.filipinoTranslation?.summary || draft.summary),
            keyPoints: isCurrent
              ? ((editedFilipinoKeyPoints.length > 0 ? editedFilipinoKeyPoints : editedKeyPoints).filter((p) => p && p.trim()))
              : (draft.filipinoTranslation?.keyPoints || draft.keyPoints),
            ordinanceNumber: isCurrent ? editedOrdinanceNumber.trim() : draft.ordinanceNumber,
            legalLevel: isCurrent ? (editedScopeLevel.trim() || draft.legalLevel || "National") : draft.legalLevel,
          },
        };
      });

      await confirmDocuments(allConfirmed);
      toast.success(`All ${allConfirmed.length} documents confirmed and saved successfully!`);
      draftEditsRef.current.isConfirmedOrDeleted = true;
      clearDraftDocuments();
      closeDocInfoModal(false);

      await fetchDocuments();
    } catch (err) {
      console.error("Failed to confirm all documents:", err);
      toast.error(formatAiUserFacingError(err.message || "Failed to confirm documents."));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDoc(doc);
    setDocViewMode("original");
    setSummaryLanguage("en");
    setFilipinoTranslation(null);
    setIsTranslationLoading(false);
    setIsEditMode(false);
    setIsDocInfoModalOpen(true);
  };

  const loadFilipinoTranslation = async (docData) => {
    const translationId = docData?.id || docData?.firestoreId;
    if (!translationId) return;

    setIsTranslationLoading(true);
    try {
      const existing = await fetchDocumentFilipinoTranslation(translationId);
      if (existing?.translation) {
        setFilipinoTranslation(existing.translation);
        return;
      }

      const generated = await translateDocumentFilipino(translationId);
      setFilipinoTranslation(generated?.translation || null);
    } catch (error) {
      if (
        String(error?.message || "")
          .toLowerCase()
          .includes("translation not found")
      ) {
        try {
          const generated = await translateDocumentFilipino(translationId);
          setFilipinoTranslation(generated?.translation || null);
        } catch (generateError) {
          console.error(
            "Failed to generate Filipino translation:",
            generateError,
          );
          toast.error(
            formatAiUserFacingError(generateError?.message || "Failed to translate to Filipino"),
          );
          setFilipinoTranslation(null);
        }
      } else {
        console.error("Failed to load Filipino translation:", error);
        toast.error(formatAiUserFacingError(error?.message || "Failed to translate to Filipino"));
        setFilipinoTranslation(null);
      }
    } finally {
      setIsTranslationLoading(false);
    }
  };

  const handleEnterEditMode = (doc) => {
    setEditedSummary(doc.summary || doc.purpose || "");
    setEditedKeyPoints(Array.isArray(doc.keyPoints) ? doc.keyPoints : []);
    setEditedFilipinoSummary(
      filipinoTranslation?.summary || doc?.filipinoTranslation?.summary || ""
    );
    setEditedFilipinoKeyPoints(
      filipinoTranslation?.keyPoints || doc?.filipinoTranslation?.keyPoints || []
    );
    setEditedTitle(summaryLanguage === "fil" ? (filipinoTranslation?.title || doc?.filipinoTranslation?.title || doc.title || "") : (doc.title || ""));
    setEditedFilipinoTitle(filipinoTranslation?.title || doc?.filipinoTranslation?.title || doc.title || "");
    setEditedOrdinanceNumber(doc.ordinanceNumber || "");
    setEditedScopeLevel(
      doc.legalLevel ||
        (doc.location?.barangay
          ? "Barangay"
          : doc.location?.municipality
            ? "Municipal"
            : "National")
    );
    setEditedCategory(doc.category || "");
    setIsCustomCategory(false);
    const curCitation = doc.citation || doc.ordinanceNumber || doc.title || "";
    setEditedCitation(curCitation);
    const curSource = doc.sourceUrl || doc.source || "";
    const isOnline = curSource.startsWith("http://") || curSource.startsWith("https://") || doc.sourceType === "online";
    setEditedSourceType(isOnline ? "online" : "office");
    setEditedSourceUrl(curSource || (isOnline ? "" : "Office of the Sangguniang Bayan Records"));
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedSummary("");
    setEditedKeyPoints([]);
    setEditedFilipinoSummary("");
    setEditedFilipinoKeyPoints([]);
    setEditedTitle("");
    setEditedFilipinoTitle("");
    setEditedOrdinanceNumber("");
    setEditedScopeLevel("");
    setEditedCategory("");
    setIsCustomCategory(false);
    setEditedCitation("");
    setEditedSourceType("office");
    setEditedSourceUrl("");
  };

  const handleSaveEdits = async () => {
    if (!selectedDoc || isSavingEdits) return;

    if (!canSubmitAnalysisForm) {
      toast.error(analysisFormValidation.message);
      return;
    }

    const docId = selectedDoc.id || selectedDoc.firestoreId;
    if (!docId) {
      toast.error("Document ID is missing.");
      return;
    }

    setIsSavingEdits(true);
    try {
      const { updateDocumentAnalysisSummary } = await import(
        "../../services/documentService"
      );
      const validationBadge = getAdminVerificationBadgeText(user);

      const result = await updateDocumentAnalysisSummary(docId, {
        summary: editedSummary.trim(),
        keyPoints: editedKeyPoints.filter((p) => p && p.trim()),
        filipinoSummary: editedFilipinoSummary.trim(),
        filipinoKeyPoints: editedFilipinoKeyPoints.filter((p) => p && p.trim()),
        title: editedTitle.trim(),
        ordinanceNumber: editedOrdinanceNumber.trim(),
        legalLevel: editedScopeLevel.trim(),
        category: editedCategory.trim(),
        citation: editedCitation.trim(),
        sourceUrl: editedSourceUrl.trim(),
        sourceType: editedSourceType,
        verifiedBy: validationBadge,
      });

      if (result?.ok) {
        setSelectedDoc({
          ...selectedDoc,
          summary: result.document?.summary || selectedDoc.summary,
          keyPoints: result.document?.keyPoints || selectedDoc.keyPoints,
          title: result.document?.title || selectedDoc.title,
          ordinanceNumber: result.document?.ordinanceNumber || selectedDoc.ordinanceNumber,
          legalLevel: result.document?.legalLevel || selectedDoc.legalLevel,
          category: result.document?.category || selectedDoc.category,
          citation: result.document?.citation || editedCitation.trim(),
          sourceUrl: result.document?.sourceUrl || editedSourceUrl.trim(),
          sourceType: result.document?.sourceType || editedSourceType,
          verifiedBy: result.document?.verifiedBy || validationBadge,
        });

        if (result.translation) {
          setFilipinoTranslation({
            ...filipinoTranslation,
            summary: result.translation?.summary || filipinoTranslation?.summary,
            keyPoints:
              result.translation?.keyPoints || filipinoTranslation?.keyPoints,
            title: result.translation?.title || filipinoTranslation?.title,
            ordinanceNumber: result.translation?.ordinanceNumber || filipinoTranslation?.ordinanceNumber,
            legalLevel: result.translation?.legalLevel || filipinoTranslation?.legalLevel,
          });
        }

        toast.success("AI Analysis Summary updated successfully!");
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Failed to save edits:", error);
      toast.error(formatAiUserFacingError(error?.message || "Failed to save changes"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleDeleteDocument = (doc) => {
    setDocumentToDelete(doc || selectedDoc);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete || isDeletingDocument) return;

    if (documentToDelete._isDraft) {
      const remainingDrafts = draftDocuments.filter(
        (d, idx) => {
          if (documentToDelete?.id && (d.id === documentToDelete.id || d._id === documentToDelete.id)) return false;
          if (d === documentToDelete) return false;
          if (idx === currentDraftIndex) return false;
          return true;
        }
      ).map((d, i) => ({ ...d, _draftQueueIndex: i }));

      toast.info("Document discarded from queue.");
      setIsDeleteModalOpen(false);
      setDocumentToDelete(null);

      if (remainingDrafts.length > 0) {
        setDraftDocuments(remainingDrafts);
        const nextIndex = Math.min(currentDraftIndex, remainingDrafts.length - 1);
        setActiveDraftIndex(nextIndex);
        openDraftDocument(remainingDrafts[nextIndex], nextIndex, remainingDrafts);
      } else {
        draftEditsRef.current.isConfirmedOrDeleted = true;
        clearDraftDocuments();
        closeDocInfoModal(false);
      }
      return;
    }

    setIsDeletingDocument(true);
    try {
      const deleteId = documentToDelete.id || documentToDelete.firestoreId;
      if (!deleteId) {
        throw new Error("Document ID is missing.");
      }

      await deleteDocument(deleteId);
      toast.success(
        `Broadcasting update: ${documentToDelete.filename || documentToDelete.id} has been removed.`,
      );

      setIsDeleteModalOpen(false);
      setIsDocInfoModalOpen(false);
      setDocumentToDelete(null);
      setSelectedDoc(null);
    } catch (err) {
      toast.error("Failed to delete document");
      console.error(err);
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const closeDeleteModal = () => {
    if (isDeletingDocument) return;
    setIsDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const closeDocInfoModal = (shouldSaveDraft = false) => {
    if (shouldSaveDraft && selectedDoc?._isDraft && !draftEditsRef.current.isConfirmedOrDeleted) {
      saveCurrentDraftEdits();
    }
    setIsDraftReviewOpen(false);
    setIsDocInfoModalOpen(false);
    setSelectedDoc(null);
    setDocViewMode("original");
    setSummaryLanguage("en");
    setFilipinoTranslation(null);
    setIsTranslationLoading(false);
    setIsEditMode(false);
    setIsCustomCategory(false);
  };

  const getPdfUrl = (doc) => doc?.pdfUrl || doc?.fileUrl || "";

  const applyFilter = (document) => {
    const documentCategory = String(document.category || "")
      .trim()
      .toLowerCase();
    const selectedCategory = String(filterOptions.category || "")
      .trim()
      .toLowerCase();
    if (selectedCategory && documentCategory !== selectedCategory) {
      return false;
    }

    const documentLevel = String(
      document.legalLevel ||
        (document.location?.barangay
          ? "Barangay"
          : document.location?.municipality
            ? "LGU"
            : "National"),
    )
      .trim()
      .toLowerCase();
    const selectedLevel = String(filterOptions.legalLevel || "")
      .trim()
      .toLowerCase()
      .replace(/^lgu$/, "municipal");
    const normalizedDocumentLevel = documentLevel === "lgu" ? "municipal" : documentLevel;
    if (selectedLevel && normalizedDocumentLevel !== selectedLevel) {
      return false;
    }

    const selectedProvince = String(filterOptions.province || "").trim().toLowerCase();
    const documentProvince = String(document.location?.province || "").trim().toLowerCase();
    if (selectedProvince && documentProvince !== selectedProvince) {
      return false;
    }

    const selectedMunicipality = String(filterOptions.municipality || "").trim().toLowerCase();
    const documentMunicipality = String(document.location?.municipality || "").trim().toLowerCase();
    if (selectedMunicipality && documentMunicipality !== selectedMunicipality) {
      return false;
    }

    const documentBarangay = String(
      document.location?.barangay || document.barangay || "",
    )
      .trim()
      .replace(/^Barangay\s+/i, "");
    const selectedBarangay = String(filterOptions.barangay || "")
      .trim()
      .replace(/^Barangay\s+/i, "");
    if (
      selectedBarangay &&
      selectedBarangay !== "" &&
      documentBarangay !== selectedBarangay
    ) {
      return false;
    }
    return true;
  };

  // Dynamically compute unique provinces from database documents only
  const availableProvinces = React.useMemo(() => {
    const provinces = new Set();
    allDocuments.forEach((doc) => {
      if (doc.location?.province) {
        provinces.add(doc.location.province);
      }
    });
    return Array.from(provinces).sort((a, b) => a.localeCompare(b));
  }, [allDocuments]);

  // Dynamically compute unique municipalities from database documents only
  const availableMunicipalities = React.useMemo(() => {
    const municipalities = new Set();
    allDocuments.forEach((doc) => {
      if (doc.location?.municipality) {
        municipalities.add(doc.location.municipality);
      }
    });
    return Array.from(municipalities).sort((a, b) => a.localeCompare(b));
  }, [allDocuments]);

  // Dynamically compute unique barangays from database documents only
  const availableBarangays = React.useMemo(() => {
    const barangays = new Set();
    allDocuments.forEach((doc) => {
      const brgy = String(doc.location?.barangay || doc.barangay || "")
        .trim()
        .replace(/^Barangay\s+/i, "");
      if (brgy) barangays.add(brgy);
    });
    return Array.from(barangays).sort((a, b) => a.localeCompare(b));
  }, [allDocuments]);

  const filteredDocuments = allDocuments.filter((doc) => {
    const searchableText =
      `${doc.filename} ${doc.title || ""} ${doc.ordinanceNumber || ""}`.toLowerCase();
    const matchesSearch = searchableText.includes(searchTerm.toLowerCase());
    const matchesFilter = applyFilter(doc);
    return matchesSearch && matchesFilter;
  });

  const openFilterModal = () => {
    setPendingFilterOptions(filterOptions);
    setShowFilterModal(true);
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  const applyPendingFilters = () => {
    setFilterOptions(pendingFilterOptions);
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const resetPendingFilters = () => {
    setPendingFilterOptions(defaultFilters);
  };

  const hasActiveFilters =
    filterOptions.category ||
    filterOptions.legalLevel ||
    filterOptions.province ||
    filterOptions.municipality ||
    filterOptions.barangay;

  // Sort by date (newest on top)
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    const dateA = a.uploadedAt?.seconds || 0;
    const dateB = b.uploadedAt?.seconds || 0;
    return dateB - dateA;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedDocuments.length / itemsPerPage);
  const paginatedDocuments = sortedDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Resolve uploader UIDs to display names/emails for sysadmin view
  React.useEffect(() => {
    if (user?.role !== "system_admin") return;
    const uids = new Set();
    paginatedDocuments.forEach((d) => {
      if (d.uploadedBy && typeof d.uploadedBy === "string") uids.add(d.uploadedBy);
    });

    const missing = Array.from(uids).filter((uid) => !uploaderCache[uid]);
    if (missing.length === 0) return;

    (async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (uid) => {
          try {
            const userDoc = await getDoc(firestoreDoc(db, "users", uid));
            if (userDoc.exists()) {
              const data = userDoc.data() || {};
              updates[uid] = {
                name: data.name || (data.user && data.user.name) || null,
                email: data.email || (data.user && data.user.email) || null,
                username: data.username || (data.user && data.user.username) || null,
              };
            } else {
              updates[uid] = { name: null, email: null, username: null };
            }
          } catch (err) {
            updates[uid] = { name: null, email: null, username: null };
          }
        }),
      );
      setUploaderCache((prev) => ({ ...prev, ...updates }));
    })();
  }, [paginatedDocuments, user, uploaderCache]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to dataset list section top
    const container = document.querySelector(".dataset-items-container");
    if (container) container.scrollTop = 0;
  };

  const formatUploadTime = (uploadedAt) => {
    const now = new Date();
    if (!uploadedAt)
      return now.toLocaleString([], {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

    // Handle Firestore Timestamp (seconds/nanoseconds)
    if (uploadedAt.seconds) {
      return new Date(uploadedAt.seconds * 1000).toLocaleString([], {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // Handle other date formats (ISO strings, etc.)
    const date = new Date(uploadedAt);
    return isNaN(date.getTime())
      ? now.toLocaleString([], {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : date.toLocaleString([], {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  const isAnalysisPhase =
    uploadPhase === "processing" || uploadPhase === "processing-current";
  const clampedUploadProgress = Math.max(0, Math.min(100, uploadProgress));
  const uploadStatusLabel =
    uploadPhase === "uploading"
      ? "Uploading files"
      : uploadPhase === "processing-current"
        ? "Processing file"
      : uploadPhase === "processing"
        ? "Analyzing document"
        : "Preparing files";
  const uploadStatusMeta =
    uploadPhase === "uploading"
      ? `${uploadingFiles.length} file${uploadingFiles.length === 1 ? "" : "s"} uploading`
      : uploadPhase === "processing-current"
        ? "Upload paused while the current file is analyzed"
      : uploadPhase === "processing"
        ? "Upload complete. Analyzing content..."
        : "Preparing files for upload";

  return (
    <div className="dashboard-container">
      <Sidebar />

      <main className="main-content">
        <div className="header-row">
          <div className="search-section">
            <div className="search-wrapper">
              <Icon icon={Search} size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search Documents..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setSearchTerm(searchInput);
                    setCurrentPage(1);
                  }
                }}
                className="search-input"
              />
              <button
                className={`doc-filter-toggle-btn ${showFilterModal ? "active" : ""}`}
                onClick={showFilterModal ? closeFilterModal : openFilterModal}
                title="Filter & Sort"
              >
                <Icon icon={Filter} size={18} />
              </button>

              {showFilterModal && (
                <>
                  <div
                    className="doc-filter-modal-overlay"
                    onClick={closeFilterModal}
                  />
                  <div className="doc-filter-modal-content">
                    <div className="doc-filter-modal-header">
                      <span className="doc-filter-modal-title">
                        Filter Documents
                      </span>
                      <button
                        className="doc-filter-modal-close-icon"
                        onClick={closeFilterModal}
                      >
                        <Icon icon={X} size={16} />
                      </button>
                    </div>

                    <div className="doc-filter-modal-body">
                      <div className="doc-filter-group">
                        <label className="doc-filter-label">Category</label>
                        <select
                          className="doc-filter-select"
                          value={pendingFilterOptions.category}
                          onChange={(e) =>
                            setPendingFilterOptions({
                              ...pendingFilterOptions,
                              category: e.target.value,
                            })
                          }
                        >
                          <option value="">All Categories</option>
                          {categoryOptions.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>

                      {(user?.role === 'system_admin' || user?.adminaccessLevel === 'provincial' || user?.adminaccessLevel === 'municipal') && (
                        <div className="doc-filter-group">
                          <label className="doc-filter-label">Level</label>
                          <select
                            className="doc-filter-select"
                            value={pendingFilterOptions.legalLevel}
                            onChange={(e) =>
                              setPendingFilterOptions({
                                ...pendingFilterOptions,
                                legalLevel: e.target.value,
                              })
                            }
                          >
                            <option value="">All Law Levels</option>
                            {(user?.role === 'system_admin' || !user?.adminaccessLevel || user?.adminaccessLevel === 'none') && (
                              <option value="National">National Law</option>
                            )}
                            {user?.role === 'system_admin' && (
                              <option value="Provincial">Provincial Ordinance</option>
                            )}
                            {(user?.role === 'system_admin' || user?.adminaccessLevel === 'provincial') && (
                              <option value="Municipal">Municipal Ordinance</option>
                            )}
                            {(user?.role === 'system_admin' || user?.adminaccessLevel === 'provincial' || user?.adminaccessLevel === 'municipal') && (
                              <option value="Barangay">Barangay Ordinance</option>
                            )}
                          </select>
                        </div>
                      )}

                      {user?.role === 'system_admin' && (
                        <div className="doc-filter-group">
                          <label className="doc-filter-label">Province</label>
                          <select
                            className="doc-filter-select"
                            value={pendingFilterOptions.province}
                            onChange={(e) =>
                              setPendingFilterOptions({
                                ...pendingFilterOptions,
                                province: e.target.value,
                                municipality: "", // Reset municipality when province changes
                                barangay: "", // Reset barangay when province changes
                              })
                            }
                          >
                            <option value="">All Provinces</option>
                            {availableProvinces.map((prov) => (
                              <option key={prov} value={prov}>
                                {prov}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {(user?.role === 'system_admin' || user?.adminaccessLevel === 'provincial' || !user?.adminaccessLevel || user?.adminaccessLevel === 'none') && (
                        <div className="doc-filter-group">
                          <label className="doc-filter-label">Municipality</label>
                          <select
                            className="doc-filter-select"
                            value={pendingFilterOptions.municipality}
                            onChange={(e) =>
                              setPendingFilterOptions({
                                ...pendingFilterOptions,
                                municipality: e.target.value,
                                barangay: "", // Reset barangay when municipality changes
                              })
                            }
                          >
                            <option value="">All Municipalities</option>
                            {availableMunicipalities.map((muni) => (
                              <option key={muni} value={muni}>
                                {muni}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {(user?.role === 'system_admin' || user?.adminaccessLevel === 'provincial' || user?.adminaccessLevel === 'municipal' || user?.adminaccessLevel === 'none' || !user?.adminaccessLevel) && (
                        <div className="doc-filter-group">
                          <label className="doc-filter-label">Barangay</label>
                          <select
                            className="doc-filter-select"
                            value={pendingFilterOptions.barangay}
                            onChange={(e) =>
                              setPendingFilterOptions({
                                ...pendingFilterOptions,
                                barangay: e.target.value,
                              })
                            }
                          >
                            <option value="">All Barangays</option>
                            {availableBarangays.map((barangay) => (
                              <option key={barangay} value={barangay}>
                                {barangay.startsWith('Barangay') ? barangay : `Barangay ${barangay}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="doc-filter-modal-footer">
                      <button
                        className="doc-filter-reset-btn"
                        onClick={resetPendingFilters}
                      >
                        Reset
                      </button>
                      <button
                        className="doc-filter-apply-btn"
                        onClick={applyPendingFilters}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DocumentManagementAiDisclaimer />

        <div className="upload-section">
          <div className="upload-drop-area">
            <input
              type="file"
              id="file-upload"
              ref={fileInputRef}
              multiple
              accept=".pdf,image/*"
              onChange={handleFileSelection}
              className="file-input-hidden"
              disabled={isScanning}
            />
            <label
              htmlFor="file-upload"
              className={`upload-dashed-box ${isScanning ? "disabled" : ""}`}
            >
              <div className="upload-cloud-icon">
                <Icon icon={Upload} size={48} color="#31356e" />
              </div>
              <div className="upload-drop-text">
                Drag & drop files or <span className="browse-link">Browse</span>
              </div>
              <div className="upload-supported-text">
                Supported formats: JPEG, PNG, GIF, MP4, PDF, PSD, AI, Word, PPT
              </div>
            </label>
          </div>

          {isScanning && (
            <div className="upload-status-group">
              <div className="upload-progress-summary" aria-live="polite">
                <div className="upload-progress-header">
                  <span className="status-label">{uploadStatusLabel}</span>
                  {!isAnalysisPhase && (
                    <span className="upload-progress-count">
                      {Math.round(clampedUploadProgress)}%
                    </span>
                  )}
                </div>
                <div
                  className={`upload-progress-track ${isAnalysisPhase ? "analysis-progress-track" : ""}`}
                  role="progressbar"
                  aria-label={isAnalysisPhase ? "Analysis progress" : "Upload progress"}
                  aria-valuemin={isAnalysisPhase ? undefined : 0}
                  aria-valuemax={isAnalysisPhase ? undefined : 100}
                  aria-valuenow={isAnalysisPhase ? undefined : Math.round(clampedUploadProgress)}
                >
                  <div
                    className={`upload-progress-fill ${isAnalysisPhase ? "analysis-progress-fill" : ""}`}
                    style={isAnalysisPhase ? undefined : { width: `${clampedUploadProgress}%` }}
                  />
                </div>
                <div className="upload-progress-meta">
                  {uploadStatusMeta}
                </div>
              </div>
              <div className="status-list">
                {uploadingFiles.slice(0, 5).map((name, idx) => (
                  <div key={idx} className="status-item uploading-item">
                    <span className="status-file-name">{name}</span>
                  </div>
                ))}
                {uploadingFiles.length > 5 && (
                  <button 
                    type="button"
                    className="view-more-uploads-btn"
                    onClick={() => setIsUploadsModalOpen(true)}
                  >
                    + {uploadingFiles.length - 5} more (View all)
                  </button>
                )}
              </div>
            </div>
          )}

          {selectedFiles.length > 0 && !isScanning && (
            <div className="upload-status-group">
              <div className="status-label">Uploaded</div>
              <div className="status-list">
                {selectedFiles.slice(0, 5).map((file, idx) => (
                  <div key={idx} className="status-item uploaded-item">
                    <span className="status-file-name">{file.name}</span>
                    <button
                      type="button"
                      className="status-remove-btn"
                      onClick={() => removeSelectedFile(idx)}
                    >
                      <Icon icon={Trash2} size={16} color="#ef4444" />
                    </button>
                  </div>
                ))}
                {selectedFiles.length > 5 && (
                  <button 
                    type="button"
                    className="view-more-uploads-btn"
                    onClick={() => setIsUploadsModalOpen(true)}
                  >
                    + {selectedFiles.length - 5} more (View all)
                  </button>
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            className="upload-submit-btn"
            disabled={isScanning || selectedFiles.length === 0}
            title={selectedFiles.length === 0 ? "Select files first" : undefined}
            onClick={handleConfirmUpload}
          >
            {isScanning ? "SCANNING..." : "UPLOAD FILES"}
          </button>

          {draftDocuments.length > 0 && (
            <div className="upload-draft-queue-box">
              <div className="draft-queue-header">
                <div className="draft-queue-title">
                  <Icon icon={FileText} size={15} color="#31356E" />
                  <span>Pending Queue ({draftDocuments.length})</span>
                </div>
                <button 
                  type="button" 
                  className="draft-queue-clear-btn" 
                  onClick={() => {
                    draftEditsRef.current.isConfirmedOrDeleted = true;
                    clearDraftDocuments();
                    if (selectedDoc?._isDraft) {
                      closeDocInfoModal(false);
                    }
                    toast.info("Upload queue cleared.");
                  }}
                  title="Clear queue"
                >
                  Discard
                </button>
              </div>
              <p className="draft-queue-desc">
                {draftDocuments.length} document{draftDocuments.length > 1 ? "s" : ""} awaiting review &amp; confirmation.
              </p>
              {queueHasDuplicates && (
                <p style={{ color: "#DC2626", fontSize: "12px", fontWeight: "600", marginTop: "4px", marginBottom: "8px" }}>
                  One or more documents match existing records in the database.
                </p>
              )}
              <button
                type="button"
                className="draft-queue-review-btn"
                onClick={() => {
                  const targetIdx =
                    activeDraftIndex >= 0 && activeDraftIndex < draftDocuments.length
                      ? activeDraftIndex
                      : 0;
                  setIsDraftReviewOpen(true);
                  openDraftDocument(draftDocuments[targetIdx] || draftDocuments[0], targetIdx, draftDocuments);
                }}
              >
                Review &amp; Confirm Details
              </button>
            </div>
          )}
        </div>

        <div className="dataset-list-section">
          <div className="dataset-list-header">
            <h2 className="dataset-list-title">Dataset List</h2>
            {hasActiveFilters && <span className="filter-badge"></span>}
          </div>
          <p className="dataset-list-subtitle">List of added documents</p>

          <div className="dataset-items-container">
            {paginatedDocuments.length > 0 ? (
              paginatedDocuments.map((doc, index) => (
                <div
                  key={index}
                  className="dataset-item"
                  onClick={() => handleViewDocument(doc)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="dataset-item-left">
                    <div className="dataset-item-info">
                      <div className="dataset-item-title-row">
                        <span className="dataset-item-filename">
                          {doc.simplifiedTitle ||
                            doc.title ||
                            doc.filename ||
                            doc.id}
                        </span>
                        {doc.ordinanceNumber && (
                          <span className="dataset-item-badge">
                            {doc.ordinanceNumber}
                          </span>
                        )}
                        {(doc.legalLevel ||
                          doc.location?.barangay ||
                          doc.location?.municipality) && (
                          <>
                            <span
                              className={`dataset-item-level-badge level-${(
                                doc.legalLevel ||
                                (doc.location?.barangay
                                  ? "Barangay"
                                  : doc.location?.municipality
                                    ? "LGU"
                                    : "National")
                              ).toLowerCase()}`}
                            >
                              {doc.legalLevel === "LGU"
                                ? "Municipal"
                                : doc.legalLevel === "Provincial"
                                  ? "Provincial"
                                  : doc.legalLevel ||
                                    (doc.location?.barangay
                                      ? "Barangay"
                                      : doc.location?.municipality
                                        ? "Municipal"
                                        : "National")}
                            </span>
                            {doc.legalLevel !== "Provincial" && doc.location?.province && (
                              <span className="dataset-item-province-badge">
                                {doc.location.province}
                              </span>
                            )}
                            {doc.legalLevel !== "Provincial" && doc.location?.municipality && (
                              <span className="dataset-item-municipality-badge">
                                {doc.location.municipality}
                              </span>
                            )}
                            {doc.legalLevel !== "Provincial" && doc.location?.barangay && (
                              <span className="dataset-item-barangay-badge">
                                {String(doc.location.barangay).replace(
                                  /^Barangay\s+/i,
                                  "",
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <span className="dataset-item-subtitle">{`${doc.filename || "Untitled"} • ${doc.category || "Uncategorized"}`}</span>
                    </div>
                  </div>
                  <div className="dataset-item-right">
                    <span className="dataset-item-meta">
                      {doc.metadata?.pages && (
                        <>
                          <span className="meta-item">
                            {doc.metadata.pages} pages
                          </span>
                          <span className="meta-dot">•</span>
                        </>
                      )}
                      <span className="meta-item">
                        {(doc.fileSize / (1024 * 1024)).toFixed(2)} MB
                      </span>
                      <span className="meta-dot">•</span>
                      <span className="meta-item">
                        Uploaded: {formatUploadTime(doc.uploadedAt)}
                      </span>
                      {user?.role === 'system_admin' && (
                        <>
                          <span className="meta-dot">•</span>
                          <span className="meta-item">
                            Uploaded by: {
                              doc.uploaderAnonymized || doc.uploadedBy === 'Deleted User'
                                ? 'Deleted User'
                                : (uploaderCache[doc.uploadedBy] && (uploaderCache[doc.uploadedBy].username || uploaderCache[doc.uploadedBy].name || uploaderCache[doc.uploadedBy].email)) || doc.uploadedBy || 'Deleted User'
                            }
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-documents">
                <Icon icon={FileText} size={48} className="no-documents-icon" />
                <span>No documents found</span>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &lt;
              </button>

              <div className="pagination-pages">
                {(() => {
                  const maxVisiblePages = 20;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = startPage + maxVisiblePages - 1;

                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      className={`page-number ${currentPage === page ? "active" : ""}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ));
                })()}
              </div>

              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </main>

      {isDocInfoModalOpen && selectedDoc && (
        <div className="doc-info-modal-overlay" onClick={() => closeDocInfoModal(true)}>
          <div
            className="doc-info-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="doc-info-modal-header-row">
              <div className="header-title-with-badge">
                <h2 className="doc-info-modal-title">Document Details</h2>
                {selectedDoc?._isDraft && detectedDuplicate && (
                  <span className="draft-queue-duplicate-tag">
                    <Icon icon={AlertTriangle} size={12} />
                    Duplicate
                  </span>
                )}
                {selectedDoc?._isDraft && draftDocuments.length > 1 && (
                  <div className="draft-queue-pill">
                    <button
                      type="button"
                      className="queue-nav-arrow"
                      disabled={currentDraftIndex === 0}
                      onClick={() => {
                        saveCurrentDraftEdits();
                        const newIndex = currentDraftIndex - 1;
                        setActiveDraftIndex(newIndex);
                        openDraftDocument(draftDocuments[newIndex], newIndex, draftDocuments);
                      }}
                      title="Previous queued document"
                    >
                      &lt;
                    </button>
                    <span>Queued {currentDraftIndex + 1} of {draftDocuments.length}</span>
                    <button
                      type="button"
                      className="queue-nav-arrow"
                      disabled={currentDraftIndex === draftDocuments.length - 1}
                      onClick={() => {
                        saveCurrentDraftEdits();
                        const newIndex = currentDraftIndex + 1;
                        setActiveDraftIndex(newIndex);
                        openDraftDocument(draftDocuments[newIndex], newIndex, draftDocuments);
                      }}
                      title="Next queued document"
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
              <button
                className="doc-info-modal-close"
                onClick={() => closeDocInfoModal(true)}
              >
                &times;
              </button>
            </div>

            <div className="doc-info-modal-body">
              {/* Secure Metadata Section (Replaces the "Generated Paper") */}
              <div
                className="extracted-analysis-container"
                style={{
                  background: "white",
                  padding: "30px",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                {selectedDoc?._isDraft && detectedDuplicate && (
                  <div className="doc-duplicate-alert-banner">
                    <Icon icon={AlertTriangle} size={22} color="#DC2626" className="duplicate-alert-icon" />
                    <div className="duplicate-alert-content">
                      <h4 className="duplicate-alert-heading">Uy, may ganito na sa database! (Duplicate Document Detected)</h4>
                      <p className="duplicate-alert-msg">
                        This document matches an existing record in the database:{" "}
                        <strong>{detectedDuplicate.ordinanceNumber || detectedDuplicate.title}</strong>
                        {detectedDuplicate.filename ? ` (${detectedDuplicate.filename})` : ""}.
                        Please update its details (e.g. Document Number or Title), or discard this draft to avoid duplicates.
                      </p>
                    </div>
                  </div>
                )}

                <div className="doc-view-toggle-row">
                  <button
                    type="button"
                    className={`doc-view-toggle-btn ${docViewMode === "original" ? "active" : ""}`}
                    onClick={() => setDocViewMode("original")}
                  >
                    Original Document
                  </button>
                  <button
                    type="button"
                    className={`doc-view-toggle-btn ${docViewMode === "summary" ? "active" : ""}`}
                    onClick={() => setDocViewMode("summary")}
                  >
                    AI Analysis Summary
                  </button>
                  {!selectedDoc?._isDraft && (
                    <button
                      type="button"
                      className={`doc-view-toggle-btn ${docViewMode === "comments" ? "active" : ""}`}
                      onClick={() => setDocViewMode("comments")}
                    >
                      Community Forum
                    </button>
                  )}
                </div>

                {docViewMode === "comments" && !selectedDoc?._isDraft ? (
                  <DocumentComments documentId={selectedDoc.id} isAdmin={true} />
                ) : docViewMode === "summary" ? (
                  <>
                    <div
                      className="doc-view-toggle-row"
                      style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <div style={{ display: "flex", gap: "8px", opacity: isEditMode && !selectedDoc?._isDraft ? 0.5 : 1, pointerEvents: isEditMode && !selectedDoc?._isDraft ? "none" : "auto" }}>
                        <button
                          type="button"
                          className={`doc-view-toggle-btn ${summaryLanguage === "en" ? "active" : ""}`}
                          onClick={() => setSummaryLanguage("en")}
                          disabled={isEditMode && !selectedDoc?._isDraft}
                          style={{
                            opacity: isEditMode && !selectedDoc?._isDraft ? 0.6 : 1,
                            cursor: isEditMode && !selectedDoc?._isDraft ? "not-allowed" : "pointer",
                          }}
                        >
                          English
                        </button>
                        <button
                          type="button"
                          className={`doc-view-toggle-btn ${summaryLanguage === "fil" ? "active" : ""}`}
                          onClick={() => {
                            setSummaryLanguage("fil");
                            if (!filipinoTranslation && !isTranslationLoading && !selectedDoc?._isDraft) {
                              loadFilipinoTranslation(selectedDoc);
                            }
                          }}
                          disabled={isEditMode && !selectedDoc?._isDraft}
                          style={{
                            opacity: isEditMode && !selectedDoc?._isDraft ? 0.6 : 1,
                            cursor: isEditMode && !selectedDoc?._isDraft ? "not-allowed" : "pointer",
                          }}
                        >
                          Filipino
                        </button>
                      </div>
                      {!selectedDoc?._isDraft && !isEditMode && (
                        <button
                          type="button"
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#31356E",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                          }}
                          onClick={() => handleEnterEditMode(selectedDoc)}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "#2D2E5F"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "#31356E"}
                        >
                          Edit Analysis
                        </button>
                      )}
                      {!selectedDoc?._isDraft && isEditMode && (
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            type="button"
                            disabled={isSavingEdits}
                            onClick={handleCancelEdit}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#F3F4F6",
                              color: "#374151",
                              border: "1px solid #D1D5DB",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: isSavingEdits ? "not-allowed" : "pointer",
                              transition: "background-color 0.2s",
                              opacity: isSavingEdits ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isSavingEdits) {
                                e.target.style.backgroundColor = "#E5E7EB";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSavingEdits) {
                                e.target.style.backgroundColor = "#F3F4F6";
                              }
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={isSavingEdits || !canSubmitAnalysisForm}
                            title={
                              !canSubmitAnalysisForm
                                ? analysisFormValidation.message
                                : undefined
                            }
                            onClick={handleSaveEdits}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#31356E",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: isSavingEdits || !canSubmitAnalysisForm ? "not-allowed" : "pointer",
                              transition: "background-color 0.2s",
                              opacity: isSavingEdits || !canSubmitAnalysisForm ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isSavingEdits && canSubmitAnalysisForm) {
                                e.target.style.backgroundColor = "#2D2E5F";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSavingEdits && canSubmitAnalysisForm) {
                                e.target.style.backgroundColor = "#31356E";
                              }
                            }}
                          >
                            {isSavingEdits ? "Saving..." : "Save"}
                          </button>
                        </div>
                      )}
                    </div>
                    {(selectedDoc?._isDraft || isEditMode) && (
                      <DocumentManagementAiDisclaimer compact />
                    )}

                    <div style={{ marginBottom: "14px" }}>
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#111827",
                          margin: 0,
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                        }}
                      >
                        AI Analysis Summary
                      </h3>
                    </div>

                    <div className="doc-provenance-bar">
                      <div className="doc-provenance-badge-verified">
                        {selectedDoc?.verifiedBy || getAdminVerificationBadgeText(user)}
                      </div>
                      <div className="doc-provenance-badge-aimodel">
                        Interpreted by: <span className="aimodel-name">{selectedDoc?.aiModel || process.env.REACT_APP_AIMODEL || "Model not recorded"}</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: "25px" }}>
                      <label className="doc-field-label">
                        Full Title
                        {isEditMode && <span className="doc-field-required-star">*</span>}
                      </label>
                      {isEditMode ? (
                        <input
                          type="text"
                          className={`doc-edit-input ${!String(summaryLanguage === "fil" ? editedFilipinoTitle : editedTitle).trim() ? "is-invalid" : ""}`}
                          value={summaryLanguage === "fil" ? editedFilipinoTitle : editedTitle}
                          onChange={(e) => {
                            if (summaryLanguage === "fil") {
                              setEditedFilipinoTitle(e.target.value);
                            } else {
                              setEditedTitle(e.target.value);
                            }
                          }}
                          onKeyDown={handleInputKeyDown}
                          placeholder="Enter document title..."
                        />
                      ) : (
                        <p className="doc-field-value-title">
                          {summaryLanguage === "fil"
                            ? filipinoTranslation?.title ||
                              selectedDoc?.filipinoTranslation?.title ||
                              selectedDoc.title
                            : selectedDoc.title}
                        </p>
                      )}
                    </div>

                    <div className="doc-field-grid">
                      <div>
                        <label className="doc-field-label">
                          Document Number
                          {isEditMode && <span className="doc-field-required-star">*</span>}
                        </label>
                        {isEditMode ? (
                          <input
                            type="text"
                            className={`doc-edit-input ${!String(editedOrdinanceNumber).trim() ? "is-invalid" : ""}`}
                            value={editedOrdinanceNumber}
                            onChange={(e) => setEditedOrdinanceNumber(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            placeholder="e.g. House Bill No. 1024 or Ordinance No. 2024-001"
                          />
                        ) : (
                          <p className="doc-field-value">
                            {selectedDoc.ordinanceNumber || "N/A"}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="doc-field-label">
                          Scope / Level
                          {isEditMode && <span className="doc-field-required-star">*</span>}
                        </label>
                        {isEditMode ? (
                          <select
                            className={`doc-edit-select ${!String(editedScopeLevel).trim() ? "is-invalid" : ""}`}
                            value={editedScopeLevel}
                            onChange={(e) => setEditedScopeLevel(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                          >
                            <option value="">Select Scope / Level...</option>
                            <option value="National">National</option>
                            <option value="Provincial">Provincial</option>
                            <option value="Municipal">Municipal</option>
                            <option value="Barangay">Barangay</option>
                          </select>
                        ) : (
                          <p className="doc-field-value">
                            {selectedDoc.legalLevel ||
                              (selectedDoc.location?.barangay
                                ? "Barangay"
                                : selectedDoc.location?.municipality
                                  ? "Municipal"
                                  : "National")}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="doc-field-label">
                          Category
                          {isEditMode && <span className="doc-field-required-star">*</span>}
                        </label>
                        {isEditMode ? (
                          <>
                            <select
                              className={`doc-edit-select ${!String(editedCategory).trim() ? "is-invalid" : ""}`}
                              value={isCustomCategory ? "__custom__" : (editedCategory || "")}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "__custom__") {
                                  setIsCustomCategory(true);
                                  setEditedCategory("");
                                } else {
                                  setIsCustomCategory(false);
                                  setEditedCategory(val);
                                }
                              }}
                              onKeyDown={handleInputKeyDown}
                            >
                              <option value="">Select Category...</option>
                              {categoryOptions.map((cat) => (
                                <option key={cat} value={cat}>
                                  {cat}
                                </option>
                              ))}
                              {editedCategory && !categoryOptions.includes(editedCategory) && !isCustomCategory && (
                                <option value={editedCategory}>{editedCategory}</option>
                              )}
                              <option value="__custom__">+ Enter custom category...</option>
                            </select>
                            {isCustomCategory && (
                              <div style={{ marginTop: "6px", display: "flex", gap: "6px" }}>
                                <input
                                  type="text"
                                  className={`doc-edit-input ${!String(editedCategory).trim() ? "is-invalid" : ""}`}
                                  placeholder="Type custom category..."
                                  value={editedCategory}
                                  onChange={(e) => setEditedCategory(e.target.value)}
                                  onKeyDown={handleInputKeyDown}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsCustomCategory(false);
                                    setEditedCategory(categoryOptions[0] || "");
                                  }}
                                  style={{
                                    padding: "0 10px",
                                    background: "#F3F4F6",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    color: "#4B5563",
                                    marginTop: "4px",
                                  }}
                                  title="Back to dropdown options"
                                >
                                  Select from list
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="doc-field-value">
                            {selectedDoc.category || "Uncategorized"}
                          </p>
                        )}
                      </div>
                      {!selectedDoc?._isDraft && (
                        <>
                          <div>
                            <label className="doc-field-label">Consultation by Analysis</label>
                            <p className="doc-field-value highlight">
                              {(selectedDoc.consultation?.analysisConsultation || 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <label className="doc-field-label">Consultation by Generation</label>
                            <p className="doc-field-value highlight">
                              {(selectedDoc.consultation?.generationConsultation || 0).toLocaleString()}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    <div
                      className="doc-citation-source-card"
                      style={{
                        backgroundColor: "#F9FAFB",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        padding: "16px 20px",
                        marginBottom: "24px",
                      }}
                    >
                      <div style={{ marginBottom: "16px" }}>
                        <label className="doc-field-label" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>Legal Citation / Reference</span>
                          {isEditMode && <span className="doc-field-required-star">*</span>}
                        </label>
                        {isEditMode ? (
                          <input
                            type="text"
                            className={`doc-edit-input ${!String(editedCitation).trim() ? "is-invalid" : ""}`}
                            value={editedCitation}
                            onChange={(e) => setEditedCitation(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            placeholder="e.g. City Ordinance No. 2024-001, Series of 2024, Sec. 3 or R.A. 4136, Sec. 4"
                          />
                        ) : (
                          <p className="doc-field-value" style={{ fontWeight: 600, color: "#1E293B", fontSize: "14px" }}>
                            {selectedDoc.citation || selectedDoc.ordinanceNumber || "No formal citation provided"}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="doc-field-label" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>Source</span>
                          {isEditMode && <span className="doc-field-required-star">*</span>}
                        </label>
                        {isEditMode ? (
                          <div>
                            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                              <button
                                type="button"
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  border: editedSourceType === "office" ? "2px solid #31356E" : "1px solid #D1D5DB",
                                  backgroundColor: editedSourceType === "office" ? "#EEF2FF" : "#FFFFFF",
                                  color: editedSourceType === "office" ? "#31356E" : "#4B5563",
                                }}
                                onClick={() => {
                                  setEditedSourceType("office");
                                  if (!editedSourceUrl || editedSourceUrl.startsWith("http")) {
                                    setEditedSourceUrl("Office of the Sangguniang Bayan Records");
                                  }
                                }}
                              >
                                Office Document / Records
                              </button>
                              <button
                                type="button"
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  border: editedSourceType === "online" ? "2px solid #31356E" : "1px solid #D1D5DB",
                                  backgroundColor: editedSourceType === "online" ? "#EEF2FF" : "#FFFFFF",
                                  color: editedSourceType === "online" ? "#31356E" : "#4B5563",
                                }}
                                onClick={() => {
                                  setEditedSourceType("online");
                                  if (editedSourceUrl === "Office of the Sangguniang Bayan Records") {
                                    setEditedSourceUrl("");
                                  }
                                }}
                              >
                                Official Website Link
                              </button>
                            </div>
                            <input
                              type="text"
                              className={`doc-edit-input ${!String(editedSourceUrl).trim() ? "is-invalid" : ""}`}
                              value={editedSourceUrl}
                              onChange={(e) => setEditedSourceUrl(e.target.value)}
                              onKeyDown={handleInputKeyDown}
                              placeholder={
                                editedSourceType === "online"
                                  ? "https://www.officialgazette.gov.ph/... or official website link"
                                  : "e.g. Office of the Sangguniang Panlungsod Records, LGU Archives"
                              }
                            />
                          </div>
                        ) : (
                          <div>
                            {selectedDoc.sourceUrl && (selectedDoc.sourceUrl.startsWith("http://") || selectedDoc.sourceUrl.startsWith("https://")) ? (
                              <a
                                href={selectedDoc.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  color: "#2563EB",
                                  fontWeight: 600,
                                  textDecoration: "underline",
                                  fontSize: "14px",
                                }}
                              >
                                {selectedDoc.sourceUrl} ↗
                              </a>
                            ) : (
                              <p className="doc-field-value" style={{ fontSize: "14px" }}>
                                {selectedDoc.sourceUrl || selectedDoc.source || "Official Office Records / Physical Archive"}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ marginBottom: "25px" }}>
                      <label className="doc-field-label">
                        Detailed Summary
                        {isEditMode && <span className="doc-field-required-star">*</span>}
                      </label>
                      {isEditMode ? (
                        <div style={{ marginTop: "6px" }}>
                          <small style={{ color: "#6B7280", fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "4px" }}>
                            {summaryLanguage === "en" ? "English Summary" : "Filipino Summary"}
                          </small>
                          <textarea
                            className={`doc-edit-textarea ${!String(summaryLanguage === "en" ? editedSummary : editedFilipinoSummary).trim() ? "is-invalid" : ""}`}
                            value={
                              summaryLanguage === "en"
                                ? editedSummary
                                : editedFilipinoSummary
                            }
                            onChange={(e) => {
                              if (summaryLanguage === "en") {
                                setEditedSummary(e.target.value);
                              } else {
                                setEditedFilipinoSummary(e.target.value);
                              }
                            }}
                            placeholder="Enter detailed summary..."
                            rows={4}
                          />
                        </div>
                      ) : (
                        <p className="doc-summary-text">
                          {summaryLanguage === "fil"
                            ? filipinoTranslation?.summary ||
                              selectedDoc?.filipinoTranslation?.summary ||
                              selectedDoc.summary ||
                              selectedDoc.purpose
                            : selectedDoc.summary || selectedDoc.purpose}
                        </p>
                      )}
                    </div>

                    {summaryLanguage === "fil" && isTranslationLoading && !selectedDoc?._isDraft && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#6B7280",
                          marginBottom: "12px",
                        }}
                      >
                        Loading Filipino translation...
                      </p>
                    )}

                    <div style={{ marginBottom: "25px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                        <label className="doc-field-label" style={{ marginBottom: 0 }}>
                          Key Extracted Points
                          {isEditMode && <span className="doc-field-required-star">*</span>}
                        </label>
                        {isEditMode && (
                          <button
                            type="button"
                            className="doc-add-point-btn"
                            onClick={() => {
                              if (summaryLanguage === "en") {
                                setEditedKeyPoints([...editedKeyPoints, ""]);
                              } else {
                                setEditedFilipinoKeyPoints([...editedFilipinoKeyPoints, ""]);
                              }
                            }}
                          >
                            + Add Point
                          </button>
                        )}
                      </div>

                      {isEditMode ? (
                        <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
                          <small style={{ color: "#6B7280", fontSize: "12px", fontWeight: "600" }}>
                            {summaryLanguage === "en" ? "English Key Points" : "Filipino Key Points"}
                          </small>
                          {(summaryLanguage === "en" ? editedKeyPoints : editedFilipinoKeyPoints).map((point, idx) => (
                            <div key={idx} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <span style={{ color: "#6B7280", fontSize: "12px", minWidth: "20px" }}>{idx + 1}.</span>
                              <input
                                type="text"
                                className={`doc-edit-input ${!String(point || "").trim() ? "is-invalid" : ""}`}
                                value={point}
                                onChange={(e) => {
                                  const newPoints = summaryLanguage === "en" ? [...editedKeyPoints] : [...editedFilipinoKeyPoints];
                                  newPoints[idx] = e.target.value;
                                  if (summaryLanguage === "en") {
                                    setEditedKeyPoints(newPoints);
                                  } else {
                                    setEditedFilipinoKeyPoints(newPoints);
                                  }
                                }}
                                onKeyDown={handleInputKeyDown}
                                placeholder={`Key point ${idx + 1}...`}
                                style={{ flex: 1 }}
                              />
                              <button
                                type="button"
                                className="doc-remove-point-btn"
                                onClick={() => {
                                  const newPoints = summaryLanguage === "en" ? [...editedKeyPoints] : [...editedFilipinoKeyPoints];
                                  newPoints.splice(idx, 1);
                                  if (summaryLanguage === "en") {
                                    setEditedKeyPoints(newPoints);
                                  } else {
                                    setEditedFilipinoKeyPoints(newPoints);
                                  }
                                }}
                                title="Remove point"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                          {(summaryLanguage === "en" ? editedKeyPoints : editedFilipinoKeyPoints).length === 0 && (
                            <p style={{ fontSize: "13px", color: "#9CA3AF", fontStyle: "italic" }}>
                              No key points added yet. Click &quot;+ Add Point&quot; to add one.
                            </p>
                          )}
                        </div>
                      ) : (
                        <ul className="doc-keypoints-list">
                          {(
                            (summaryLanguage === "fil"
                              ? filipinoTranslation?.keyPoints || selectedDoc?.filipinoTranslation?.keyPoints || selectedDoc.keyPoints
                              : selectedDoc.keyPoints) || []
                          ).map((point, idx) => (
                            <li key={idx}>{point}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#111827",
                          margin: 0,
                          textTransform: "uppercase",
                        }}
                      >
                        Original Document
                      </h3>
                      {getPdfUrl(selectedDoc) && (
                        <a
                          href={getPdfUrl(selectedDoc)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            padding: "6px 14px",
                            borderRadius: "6px",
                            backgroundColor: "#31356E",
                            color: "#ffffff",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          Open in New Tab ↗
                        </a>
                      )}
                    </div>
                    {getPdfUrl(selectedDoc) ? (
                      <>
                        <iframe
                          title="Original Document PDF"
                          src={getPdfUrl(selectedDoc)}
                          style={{
                            width: "100%",
                            height: "600px",
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                          }}
                        />
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#6B7280",
                            marginTop: "8px",
                            textAlign: "center",
                          }}
                        >
                          Having trouble viewing the PDF preview?{" "}
                          <a
                            href={getPdfUrl(selectedDoc)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#31356E",
                              fontWeight: 600,
                              textDecoration: "underline",
                            }}
                          >
                            Click here to open or download it in a new tab
                          </a>
                          .
                        </p>
                      </>
                    ) : (
                      <div className="original-doc-text-panel">
                        No source PDF is available for this document yet.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="doc-info-modal-footer">
              {(selectedDoc?._isDraft || isEditMode) && !canSubmitAnalysisForm && (
                <p className="doc-review-required-hint">{analysisFormValidation.message}</p>
              )}
              <button
                type="button"
                className="doc-delete-btn"
                onClick={() => handleDeleteDocument(selectedDoc)}
              >
                {selectedDoc?._isDraft ? "Delete / Discard" : "Delete Document"}
              </button>
              <button
                type="button"
                className={selectedDoc?._isDraft ? "doc-close-outline-btn" : "doc-close-btn"}
                onClick={() => closeDocInfoModal(true)}
              >
                Close
              </button>
              {selectedDoc?._isDraft && (
                <>
                  {draftDocuments.length > 1 && (
                    <button
                      type="button"
                      className="doc-confirm-all-btn"
                      onClick={handleConfirmAllDrafts}
                      disabled={isSavingEdits || queueHasDuplicates || !canSubmitAnalysisForm || queueHasIncompleteDrafts}
                      title={
                        !canSubmitAnalysisForm
                          ? analysisFormValidation.message
                          : queueHasIncompleteDrafts
                          ? "Some queued documents have missing details. Please review each document before confirming all."
                          : queueHasDuplicates
                            ? "Queue contains duplicate documents. Review or discard them first."
                            : "Confirm and save all queued documents"
                      }
                    >
                      Confirm All ({draftDocuments.length})
                    </button>
                  )}
                  <button
                    type="button"
                    className="doc-confirm-btn"
                    onClick={handleConfirmDetails}
                    disabled={isSavingEdits || !!detectedDuplicate || !canSubmitAnalysisForm}
                    title={
                      !canSubmitAnalysisForm
                        ? analysisFormValidation.message
                        : detectedDuplicate
                          ? "Cannot confirm duplicate document. Change details or discard."
                          : undefined
                    }
                  >
                    {isSavingEdits
                      ? "Saving..."
                      : detectedDuplicate
                      ? "Duplicate Detected"
                      : draftDocuments.length > 1
                      ? `Confirm Details (${currentDraftIndex + 1}/${draftDocuments.length})`
                      : "Confirm Details"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="delete-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-modal-header">
              <div className="delete-warning-icon">
                <Icon icon={AlertTriangle} size={32} />
              </div>
              <h2 className="delete-modal-title">
                {documentToDelete?._isDraft ? "Discard Queued Document?" : "Confirm Deletion"}
              </h2>
              <p className="delete-modal-subtitle">
                {documentToDelete?._isDraft
                  ? `Are you sure you want to discard "${documentToDelete?.title || documentToDelete?.filename || "this document"}" from the upload queue?`
                  : `Are you sure you want to permanently delete "${documentToDelete?.filename || documentToDelete?.title || documentToDelete?.id}"? This action cannot be undone.`}
              </p>
            </div>
            <div className="delete-modal-footer">
              <button
                className="delete-cancel-btn"
                onClick={closeDeleteModal}
                disabled={isDeletingDocument}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-btn document-delete-confirm-btn"
                onClick={confirmDeleteDocument}
                disabled={isDeletingDocument}
              >
                {isDeletingDocument
                  ? "Processing..."
                  : documentToDelete?._isDraft
                  ? "Yes, Discard"
                  : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {isUploadsModalOpen && (
        <div className="uploads-modal-overlay" onClick={() => setIsUploadsModalOpen(false)}>
          <div className="uploads-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="uploads-modal-header">
              <h2 className="uploads-modal-title">
                {isScanning ? "Uploading Files" : "Selected Files"}
              </h2>
              <button
                type="button"
                className="doc-close-btn"
                onClick={() => setIsUploadsModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <Icon icon={X} size={24} />
              </button>
            </div>
            <div className="uploads-modal-body">
              <div className="status-list">
                {isScanning
                  ? uploadingFiles.map((name, idx) => (
                      <div key={idx} className="status-item uploading-item">
                        <span className="status-file-name">{name}</span>
                      </div>
                    ))
                  : selectedFiles.map((file, idx) => (
                      <div key={idx} className="status-item uploaded-item">
                        <span className="status-file-name">{file.name}</span>
                        <button
                          type="button"
                          className="status-remove-btn"
                          onClick={() => removeSelectedFile(idx)}
                        >
                          <Icon icon={Trash2} size={16} color="#ef4444" />
                        </button>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SA_Overview2;
